import React, { Fragment } from 'react';
import { ContextualAnalyticsData, SCREEN, FireScreenAnalytics } from '@atlassian/analytics-bridge';
import { useLocation } from '@reach/router';

import { normalizeSourceName } from '../../utils/analytics';

interface PageAnalyticsProps {
	/**
	 * Title of the page, for example "Avatar".
	 */
	pageTitle: string;

	/**
	 * Names of the tabs displayed on the page.
	 * Used to get the current selected tab or default tab for analytics.
	 */
	tabNames: string[];

	/**
	 * Name of the package this documentation is for, e.g. "@atlaskit/avatar".
	 */
	packageName: string;

	/**
	 * Page component, probably.
	 */
	children: React.ReactNode;
}

const getTabNameFromLocation = (path: string, tabs: string[]): string | undefined => {
	const pathArray = path.split('/');
	const currentPage = pathArray[pathArray.length - 1];
	return tabs.includes(currentPage) ? currentPage : undefined;
};

export default function PageAnalytics(props: PageAnalyticsProps) {
	const { pathname, search } = useLocation();
	const tabName = getTabNameFromLocation(pathname, props.tabNames) || props.tabNames[0];
	const sourceName = normalizeSourceName('components', props.pageTitle, tabName);

	// Exclude synthetic Pollinator traffic from the site.
	// The "pollinatorCheck" param has been added to the check url on
	// production: https://pollinator.prod.atl-paas.net/checks/f7425790-6c31-4368-9911-3facd24cb51f
	// staging https://pollinator.prod.atl-paas.net/checks/a520babb-9d7b-4687-a36c-6d878eb739db
	const query = React.useMemo(() => new URLSearchParams(search), [search]);
	if (query.get('pollinatorCheck')) {
		return <Fragment>{props.children}</Fragment>;
	}

	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="content"
			attributes={{
				name: sourceName,
				context: 'components',
				packageName: props.packageName,
			}}
		>
			<FireScreenAnalytics key={sourceName} />
			{props.children}
		</ContextualAnalyticsData>
	);
}
